import React, { FC } from 'react';
import {
  Divider,
  TextButton,
  Popover,
  Card,
  Box,
  Button,
} from '@wix/design-system';
import { ChevronDown } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/fe-essentials-standalone/i18n';
import { ACTIONS, useExploreContext } from '../../../Contexts/ExploreContext';

type Props = {
  element: string;
  width?: number;
  minWidth?: number;
  panel: string;
  children: JSX.Element | Array<JSX.Element>;
  onApply: () => void;
  onClear: () => void;
  showOverflow?: boolean;
  numSelectedServices?: number;
  maxHeight?: string;
};

const FilterPopover: FC<Props> = ({
  element,
  children,
  width,
  minWidth,
  panel,
  onApply,
  onClear,
  showOverflow = false,
  numSelectedServices,
  maxHeight,
}) => {
  const {
    dispatch,
    state: { openedFilterPopover },
  } = useExploreContext();

  const { t } = useTranslation();
  const closePopup = () => {
    dispatch({
      type: ACTIONS.FILTER_POPOVER_STATE,
      shouldOpen: false,
    });
  };

  return (
    <Popover
      dataHook={`${panel}-popover`}
      animate
      showArrow
      appendTo="window"
      flip={false}
      onClick={() => {
        dispatch({
          type: ACTIONS.FILTER_POPOVER_STATE,
          panel,
          shouldOpen: true,
        });
        setTimeout(() => {
          (document?.querySelector('#popover-content') as HTMLElement)?.focus();
        }, 0);
      }}
      placement="bottom"
      shown={panel === openedFilterPopover}
      moveBy={{
        y: 10,
        x: 48,
      }}
      zIndex={3}
      onClickOutside={() => closePopup()}
    >
      <Popover.Element>
        <TextButton
          dataHook={`${panel}-popover-element`}
          skin="dark"
          suffixIcon={<ChevronDown />}
        >
          {element}
        </TextButton>
      </Popover.Element>
      <Popover.Content>
        <div
          tabIndex={-1}
          id="popover-content"
          style={{
            display: panel === openedFilterPopover ? 'block' : 'none',
          }}
          onKeyUp={(event) => {
            if (event.key === 'Escape') {
              closePopup();
            }
          }}
        >
          <Card hideOverflow={showOverflow ? false : true}>
            <div style={{ marginTop: showOverflow ? '0px' : '12px' }}>
              <Box
                minWidth={minWidth}
                width={width}
                direction="vertical"
                gap="12px"
                maxHeight={maxHeight}
                overflow={showOverflow ? 'visible' : 'scroll'}
                paddingLeft="24px"
                paddingRight="24px"
                paddingTop="12px"
                paddingBottom="24px"
              >
                {children}
              </Box>
              <Divider />
              <Box
                paddingRight="24px"
                paddingLeft="24px"
                paddingTop="24px"
                paddingBottom="24px"
                align="space-between"
                verticalAlign="middle"
                gap="24px"
              >
                <TextButton
                  skin="dark"
                  dataHook="clear-all-filter-button"
                  underline="always"
                  onClick={() => onClear()}
                >
                  {t('desktop-filter-clear-all-with-amount', {
                    selectedAmount: numSelectedServices,
                  })}
                </TextButton>
                <Button
                  dataHook="apply-filter-button"
                  onClick={() => {
                    onApply();
                  }}
                >
                  {t('desktop-filter-apply')}
                </Button>
              </Box>
            </div>
          </Card>
        </div>
      </Popover.Content>
    </Popover>
  );
};

export default FilterPopover;
